import { inject } from '@angular/core';
import { CanMatchFn, Route, Router } from '@angular/router';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { AUTH_ROUTES } from 'src/app/constants/auth.constants';
import { Permission } from 'src/app/enum/permission.enum';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';

/**
 * Tiempo máximo de espera a que los permisos se resuelvan tras el arranque de MSAL.
 * Si se supera, se considera que no hay permisos y se redirige a la ruta por defecto.
 */
const PERMISSIONS_LOAD_TIMEOUT_MS = 10_000;

/**
 * Protege una ruta contra los permisos declarados en `route.data.permissions`.
 *
 * `PermissionsService.getPermissions()` es un `BehaviorSubject` inicializado a `[]`,
 * por lo que resolverlo directamente devuelve el valor vacío inicial cuando la ruta
 * se evalúa antes de que MSAL haya emitido el tenant (F5, deep link o carga directa).
 * Por eso aquí se espera primero a `arePermissionsLoaded()`.
 */
export const permissionGuard: CanMatchFn = async (route: Route) => {
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);

  const requiredPermissions: Permission[] =
    (route.data?.['permissions'] as Permission[]) ?? [];

  if (requiredPermissions.length === 0) {
    return true;
  }

  try {
    await firstValueFrom(
      permissionsService.arePermissionsLoaded().pipe(
        filter((isLoaded): isLoaded is true => isLoaded),
        timeout(PERMISSIONS_LOAD_TIMEOUT_MS)
      )
    );

    const userPermissions = await firstValueFrom(
      permissionsService.getPermissions()
    );

    const hasPermission = requiredPermissions.every((permission) =>
      userPermissions.includes(permission)
    );

    if (!hasPermission) {
      return denyAndRedirect(router);
    }

    return true;
  } catch {
    return denyAndRedirect(router);
  }
};

async function denyAndRedirect(router: Router): Promise<boolean> {
  await router.navigateByUrl(AUTH_ROUTES.HOME);
  return false;
}
